import React, { useCallback, useEffect, useState } from 'react';
import { dispatchAPI, contractsAPI, inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import {
  ClipboardList, Plus, X, Loader2, AlertTriangle, CheckCircle2, ChevronRight,
  Factory, Truck, Trash2, PackageCheck, Weight, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

// One node of the exploded BOM tree.
const PlanNode = ({ node }) => (
  <div className="border-l-2 border-slate-200 pl-3 py-1" data-testid={`plan-node-${node.item_id}`}>
    <div className="flex items-center gap-2 flex-wrap text-xs">
      <span className="font-medium">{node.item_unique_id}</span>
      <span className="text-muted-foreground">{node.item_name}</span>
      <Badge variant="outline" className="text-[10px]">need {fmt(node.required)} {node.unit_of_measure}</Badge>
      {node.from_stock > 0 && (
        <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300">
          {fmt(node.from_stock)} from stock
        </Badge>
      )}
      {node.to_produce > 0 && (
        <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300">
          produce {fmt(node.to_produce)}
        </Badge>
      )}
      {node.level === 0 && (node.line_weight_kg
        ? <Badge variant="outline" className="text-[10px]" data-testid={`dp-line-weight-${node.item_id}`}>
            <Weight className="w-2.5 h-2.5 mr-0.5" /> {Number(node.line_weight_kg).toFixed(3)} kg
          </Badge>
        : <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-300">
            no weight in Item Master
          </Badge>
      )}
      {node.route_name && (
        <Badge variant="outline" className="text-[10px] text-slate-600" data-testid={`dp-node-bom-${node.item_id}`}>
          BOM: {node.route_name}
        </Badge>
      )}
      {node.creates_wo && (
        <Badge className="text-[10px] bg-blue-100 text-blue-700 border-blue-300">
          <Factory className="w-2.5 h-2.5 mr-0.5" /> draft WO
        </Badge>
      )}
      {node.blocked && (
        <Badge className="text-[10px] bg-rose-100 text-rose-700 border-rose-300">blocked</Badge>
      )}
    </div>
    {node.blocked && node.block_reason && (
      <p className="text-[11px] text-rose-600 mt-0.5">{node.block_reason}</p>
    )}
    {(node.purchased_inputs || []).length > 0 && (
      <ul className="mt-0.5">
        {node.purchased_inputs.map(i => (
          <li key={i.item_id} className="text-[11px] text-muted-foreground">
            <ChevronRight className="w-3 h-3 inline" />
            {i.item_name} — need {fmt(i.required)}, have {fmt(i.available)}
            {i.shortage > 0
              ? <span className="text-rose-600 font-medium"> (short {fmt(i.shortage)})</span>
              : <span className="text-emerald-600"> (ok)</span>}
            {i.is_raw_material && <span className="text-amber-700"> · purchased RM</span>}
          </li>
        ))}
      </ul>
    )}
    {(node.wo_chunks || []).length > 1 && (
      <p className="text-[11px] text-blue-700 mt-0.5" data-testid={`dp-split-${node.item_id}`}>
        Split into {node.wo_chunks.length} work orders because the components come from different batches
      </p>
    )}
    {(node.wo_chunks || []).map((ch, ci) => (
      <div key={ci} className="text-[11px] text-muted-foreground mt-0.5" data-testid={`dp-chunk-${node.item_id}-${ci}`}>
        <Factory className="w-3 h-3 inline mr-0.5" />
        WO {ci + 1}: {fmt(ch.quantity)} {node.unit_of_measure} · route card <span className="font-mono">{ch.route_card_number}</span>
        {(ch.batches || []).length > 0 && (
          <span> — consumes {ch.batches.map(b => `${b.item_name} ${fmt(b.quantity)} from ${b.route_card_number}`).join('; ')}</span>
        )}
      </div>
    ))}
    {(node.children || []).map(c => <PlanNode key={c.item_id + c.level} node={c} />)}
  </div>
);

export default function DispatchPlanner() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const [customerId, setCustomerId] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [requiredDate, setRequiredDate] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState([{ item_id: '', quantity: '', route_id: '', routes: [] }]);
  const [showWeight, setShowWeight] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [itemWeights, setItemWeights] = useState({});

  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyPlan, setBusyPlan] = useState('');
  const [dispatchDialog, setDispatchDialog] = useState(null); // { plan, posting }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDispatched, setShowDispatched] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cust, itm, pl] = await Promise.all([
        contractsAPI.activeCustomers(),
        inventoryAPI.listItems({ limit: 1000, make_or_buy: 'make', item_type: 'Finished Good' }),
        dispatchAPI.listDispatchPlans(showDispatched),
      ]);
      setCustomers(cust.data || []);
      setItems(itm.data?.items || itm.data || []);
      setPlans(pl.data?.plans || []);
    } catch { toast.error('Failed to load dispatch plan data'); }
    finally { setLoading(false); }
  }, [showDispatched]);

  useEffect(() => { load(); }, [load]);

  const itemOptions = items
    .filter(i => i.item_type === 'Finished Good')
    .map(i => ({
      value: i.id, label: `${i.unique_id} - ${i.item_name}`,
      sub: `${i.item_type}${i.category ? ' | ' + i.category : ''}`,
      searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''} ${i.category || ''}`,
    }));

  const payload = () => ({
    customer_id: customerId, po_number: poNumber, required_date: requiredDate, notes,
    show_weight_on_challan: showWeight,
    lines: rows.filter(r => r.item_id && parseFloat(r.quantity) > 0)
      .map(r => ({ item_id: r.item_id, quantity: parseFloat(r.quantity), route_id: r.route_id || null })),
  });

  const loadLineRoutes = async (idx, itemId) => {
    if (!itemId) return;
    try {
      const res = await inventoryAPI.getItemRoutes(itemId);
      const list = res.data?.routes || [];
      const def = (list.find(x => x.is_default) || list[0] || {}).id || '';
      setRows(rs => rs.map((x, i) => i === idx ? { ...x, routes: list, route_id: def } : x));
    } catch { /* dropdown stays empty */ }
  };

  // Per-piece weights are cached so the running total updates as quantities are typed.
  useEffect(() => {
    const missing = rows.map(r => r.item_id).filter(id => id && !(id in itemWeights));
    if (!missing.length) return;
    dispatchAPI.getDispatchItemWeights([...new Set(missing)].join(','))
      .then(res => setItemWeights(w => ({ ...w, ...(res.data?.weights || {}) })))
      .catch(() => {});
  }, [rows, itemWeights]);

  const lineWeight = (r) => {
    const qty = parseFloat(r.quantity);
    if (!r.item_id || !(qty > 0)) return 0;
    const unit = itemWeights[r.item_id]?.unit_weight_kg;
    return unit == null ? null : unit * qty;
  };

  const liveWeight = rows.reduce((acc, r) => {
    const w = lineWeight(r);
    if (w === null) {
      const name = (items.find(i => i.id === r.item_id) || {}).item_name;
      if (name && parseFloat(r.quantity) > 0) acc.missing.push(name);
    } else if (w > 0) {
      acc.total += w; acc.lines += 1;
    }
    return acc;
  }, { total: 0, lines: 0, missing: [] });

  const checkPlan = async () => {
    const body = payload();
    if (!body.customer_id) { toast.error('Select the customer this dispatch is for'); return; }
    if (!body.lines.length) { toast.error('Add at least one part with a quantity'); return; }
    setChecking(true); setPreview(null);
    try {
      const res = await dispatchAPI.previewDispatchPlan(body);
      setPreview(res.data);
      if (res.data.feasible) {
        toast.success(res.data.total_wos
          ? `Plan is clear — ${res.data.total_wos} work order(s) will be created`
          : 'Everything ships from stock — no work orders needed');
      } else {
        toast.warning(`${res.data.action_items.length} item(s) must be resolved before planning`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not check the plan');
    } finally { setChecking(false); }
  };

  const createPlan = async () => {
    setCreating(true);
    try {
      const res = editingPlan
        ? await dispatchAPI.updateDispatchPlan(editingPlan.id, payload())
        : await dispatchAPI.createDispatchPlan(payload());
      toast.success(res.data.message);
      resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not ${editingPlan ? 'update' : 'create'} the plan`, { duration: 10000 });
    } finally { setCreating(false); }
  };

  const resetForm = () => {
    setEditingPlan(null); setPreview(null); setCustomerId(''); setPoNumber('');
    setRequiredDate(''); setNotes(''); setShowWeight(false);
    setRows([{ item_id: '', quantity: '', route_id: '', routes: [] }]);
  };

  const startEdit = async (plan) => {
    setEditingPlan(plan);
    setCustomerId(plan.customer_id); setPoNumber(plan.po_number || '');
    setRequiredDate(plan.required_date || ''); setNotes(plan.notes || '');
    setShowWeight(!!plan.show_weight_on_challan);
    setPreview(null);
    setRows(plan.lines.map(ln => ({
      item_id: ln.item_id, quantity: String(ln.quantity), route_id: ln.route_id || '', routes: [],
    })));
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const lists = await Promise.all(plan.lines.map(ln =>
      inventoryAPI.getItemRoutes(ln.item_id).then(r => r.data?.routes || []).catch(() => [])));
    setRows(plan.lines.map((ln, i) => ({
      item_id: ln.item_id, quantity: String(ln.quantity),
      route_id: ln.route_id || (lists[i].find(x => x.is_default) || lists[i][0] || {}).id || '',
      routes: lists[i],
    })));
    toast.info(`Editing ${plan.plan_number} — change the lines, run "Check plan", then update`);
  };

  const releasePlan = async (plan) => {
    setBusyPlan(plan.id);
    try {
      const res = await dispatchAPI.releaseDispatchPlan(plan.id);
      toast.success(res.data.message);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not release the plan');
    } finally { setBusyPlan(''); }
  };

  const openDispatch = async (plan) => {
    setBusyPlan(plan.id);
    try {
      const res = await dispatchAPI.dispatchPlanPostingPreview(plan.id);
      setDispatchDialog({ plan, posting: res.data, emailChallan: false, toEmail: '' });
      dispatchAPI.getCustomerDispatchEmails(plan.customer_id)
        .then(r => setDispatchDialog(d => d && d.plan.id === plan.id
          ? { ...d, toEmail: (r.data?.emails || []).join(', ') } : d))
        .catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the dispatch preview');
    } finally { setBusyPlan(''); }
  };

  const confirmDispatch = async () => {
    const plan = dispatchDialog.plan;
    if (dispatchDialog.emailChallan && !dispatchDialog.toEmail.trim()) {
      toast.error('No customer email on file — enter at least one address or untick "Email challan"');
      return;
    }
    setBusyPlan(plan.id);
    try {
      const res = await dispatchAPI.produceAndDispatchPlan(plan.id, {
        email_challan: dispatchDialog.emailChallan,
        to_email: dispatchDialog.emailChallan ? dispatchDialog.toEmail.trim() : '',
      });
      toast.success(res.data.message, { duration: 10000 });
      if (res.data.email && !res.data.email.sent) {
        toast.error(`Challan created but the email failed: ${res.data.email.error}`, { duration: 14000 });
      }
      setDispatchDialog(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Produce & dispatch failed', { duration: 12000 });
    } finally { setBusyPlan(''); }
  };

  const deletePlan = async (plan) => {
    setBusyPlan(plan.id);
    try {
      await dispatchAPI.deleteDispatchPlan(plan.id);
      toast.success(`${plan.plan_number} deleted`);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete the plan');
    } finally { setBusyPlan(''); }
  };

  const statusBadge = (s) => {
    const map = {
      draft: 'bg-slate-100 text-slate-700 border-slate-300',
      ready: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      in_progress: 'bg-blue-100 text-blue-700 border-blue-300',
      produced: 'bg-violet-100 text-violet-700 border-violet-300',
      dispatched: 'bg-teal-100 text-teal-700 border-teal-300',
    };
    return <Badge className={`text-[10px] ${map[s] || map.draft}`}>{s.replace('_', ' ')}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="dispatch-planner">
      {/* Builder */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-blue-500" />
            {editingPlan ? `Edit dispatch plan ${editingPlan.plan_number}` : 'New dispatch plan'}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {editingPlan
              ? 'Change the lines, quantities or BOMs, run "Check plan" and update — the plan\'s draft work orders are rebuilt from scratch.'
              : `Enter what the customer needs. The ERP ships whatever is in stock (Finished Goods) and auto-plans draft work orders
            for the shortfall, right down the BOM tree — stopping at bought-out parts and purchased raw material.`}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Customer *</Label>
              <SearchableSelect
                options={customers.map(c => ({ value: c.id, label: c.customer_name, searchStr: c.customer_name }))}
                value={customerId} onSelect={setCustomerId}
                placeholder="Select customer" emptyText="No active customers"
                testId="dp-customer-select"
              />
            </div>
            <div>
              <Label className="text-xs">Customer PO (optional)</Label>
              <Input value={poNumber} onChange={e => setPoNumber(e.target.value)} className="h-9 text-xs"
                placeholder="PO number" data-testid="dp-po-number" />
            </div>
            <div>
              <Label className="text-xs">Required date</Label>
              <Input type="date" value={requiredDate} onChange={e => setRequiredDate(e.target.value)}
                className="h-9 text-xs" data-testid="dp-required-date" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs">Parts to dispatch <span className="text-[10px] text-muted-foreground font-normal">(finished goods only)</span></Label>
              <div className="flex items-center gap-3">
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setRows(r => [...r, { item_id: '', quantity: '', route_id: '', routes: [] }])} data-testid="dp-add-line">
                  <Plus className="w-3 h-3 mr-1" /> Add part
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {rows.map((r, idx) => (
                <div key={idx} className="flex items-end gap-2" data-testid={`dp-row-${idx}`}>
                  <div className="flex-1">
                    <SearchableSelect
                      options={itemOptions} value={r.item_id}
                      onSelect={v => { setRows(rs => rs.map((x, i) => i === idx ? { ...x, item_id: v, route_id: '', routes: [] } : x)); loadLineRoutes(idx, v); }}
                      placeholder="Select part" emptyText="No parts found" testId={`dp-item-${idx}`}
                    />
                  </div>
                  <div className="w-64">
                    <SearchableSelect
                      options={(r.routes || []).map(rt => ({
                        value: rt.id,
                        label: `${rt.route_name}${rt.revision ? ` rev ${rt.revision}` : ''} · ${rt.material_source_label}${rt.is_default ? ' (default)' : ''}`,
                        sub: rt.bom_summary ? `consumes: ${rt.bom_summary}${rt.bom_count > 3 ? ` +${rt.bom_count - 3} more` : ''}` : 'no materials on BOM',
                        searchStr: `${rt.route_name} ${rt.material_source_label} ${rt.bom_summary || ''}`,
                      }))}
                      value={r.route_id}
                      onSelect={v => setRows(rs => rs.map((x, i) => i === idx ? { ...x, route_id: v } : x))}
                      placeholder={r.item_id ? ((r.routes || []).length ? 'BOM / Route' : 'No active BOM') : 'BOM / Route'}
                      emptyText="No active BOM" testId={`dp-bom-${idx}`}
                    />
                  </div>
                  <div className="w-28">
                    <Input type="number" min="0" step="any" value={r.quantity}
                      onChange={e => setRows(rs => rs.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))}
                      className="h-9 text-xs" placeholder="Qty" data-testid={`dp-qty-${idx}`} />
                  </div>
                  <div className="w-24 pb-2 text-right">
                    <span className="text-[11px] font-medium" data-testid={`dp-line-weight-${idx}`}>
                      {lineWeight(r) === null
                        ? <span className="text-orange-600">no wt</span>
                        : `${lineWeight(r).toFixed(3)} kg`}
                    </span>
                  </div>
                  <Button size="icon" variant="ghost" className="h-9 w-9"
                    onClick={() => setRows(rs => rs.length === 1 ? rs : rs.filter((_, i) => i !== idx))}
                    data-testid={`dp-remove-${idx}`}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="text-xs"
              placeholder="Anything the shop floor should know" data-testid="dp-notes" />
          </div>

          <div className="rounded border bg-slate-50/60 p-2.5 space-y-1.5">
            <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="dp-show-weight-toggle">
              <input type="checkbox" checked={showWeight} onChange={e => setShowWeight(e.target.checked)}
                className="h-3.5 w-3.5 accent-slate-600" />
              Show per-line weights on the challan PDF (the total is always printed)
            </label>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">
                Total dispatch weight{showWeight ? ' (per-line weights also printed)' : ' (printed on the challan)'}:
              </span>
              <span className="font-semibold" data-testid="dp-total-weight">
                {liveWeight.total.toFixed(3)} kg
              </span>
              <span className="text-muted-foreground">
                {liveWeight.lines > 0 ? `across ${liveWeight.lines} line(s), updates as you type` : '— add parts and quantities'}
              </span>
            </div>
            {liveWeight.missing.length > 0 && (
              <p className="text-[11px] text-orange-600 flex items-center gap-1" data-testid="dp-weight-warning">
                <AlertTriangle className="w-3.5 h-3.5" /> No weight configured for {liveWeight.missing.join(', ')} —
                add it in Item Master; the total excludes them.
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button size="sm" onClick={checkPlan} disabled={checking} data-testid="dp-check-plan">
              {checking && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Check plan
            </Button>
            {preview && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700"
                onClick={createPlan} disabled={!preview.feasible || creating} data-testid="dp-create-plan">
                {creating && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                <Factory className="w-3.5 h-3.5 mr-1" />
                {editingPlan
                  ? (preview.total_wos ? `Update plan + ${preview.total_wos} draft WO(s)` : 'Update plan')
                  : (preview.total_wos ? `Create plan + ${preview.total_wos} draft WO(s)` : 'Create plan')}
              </Button>
            )}
            {editingPlan && (
              <Button size="sm" variant="outline" onClick={resetForm} data-testid="dp-cancel-edit">
                Cancel edit
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {preview && (
        <Card data-testid="dp-preview">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              {preview.feasible
                ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Plan is clear</>
                : <><AlertTriangle className="w-4 h-4 text-rose-600" /> Plan is blocked</>}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {preview.customer_name}
              {preview.required_date ? ` · required ${preview.required_date}` : ''}
              {preview.total_wos ? ` · ${preview.total_wos} work order(s) to create` : ' · nothing to produce'}
              {preview.total_weight_kg ? ` · ${Number(preview.total_weight_kg).toFixed(3)} kg total` : ''}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {!preview.feasible && (
              <div className="rounded border border-rose-200 bg-rose-50/70 p-2.5" data-testid="dp-action-items">
                <p className="text-xs font-semibold text-rose-700 mb-1">Resolve these first — nothing is created until then</p>
                <ul className="space-y-1.5">
                  {preview.action_items.map((a, i) => (
                    <li key={i} className="text-[11px] flex items-start justify-between gap-2" data-testid={`dp-action-${i}`}>
                      <span>
                        <Badge variant="outline" className="text-[9px] mr-1">
                          {a.type === 'level1_production' ? 'PLAN PRODUCTION'
                            : a.type === 'purchase_entry' ? 'PURCHASE ENTRY'
                              : a.type.replace('_', ' ').toUpperCase()}
                        </Badge>
                        {a.message}
                      </span>
                      {a.link && (
                        <Button size="sm" variant="outline" className="h-6 text-[10px] shrink-0"
                          onClick={() => navigate(a.link)} data-testid={`dp-action-link-${i}`}>Go</Button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {preview.lines.map(n => (
              <div key={n.item_id} className="rounded border p-2.5">
                <PlanNode node={n} />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Existing plans */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">
              {showDispatched ? 'Dispatch plans' : 'Open dispatch plans'}
            </CardTitle>
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={showDispatched}
                onChange={e => setShowDispatched(e.target.checked)}
                className="h-3.5 w-3.5 accent-slate-600" data-testid="dp-show-dispatched" />
              Show dispatched
            </label>
          </div>
        </CardHeader>
        <CardContent className="space-y-2" data-testid="dp-plan-list">
          {plans.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              {showDispatched ? 'No dispatch plans yet' : 'No open dispatch plans'}
            </p>
          ) : plans.map(p => (
            <div key={p.id} className="border rounded-lg p-3" data-testid={`dp-plan-${p.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{p.plan_number}</span>
                    {statusBadge(p.status)}
                    {p.dc_number && <Badge variant="outline" className="text-[10px]">{p.dc_number}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {p.customer_name}{p.po_number ? ` · PO ${p.po_number}` : ''}
                    {p.required_date ? ` · required ${p.required_date}` : ''}
                    {p.total_weight_kg ? ` · ${Number(p.total_weight_kg).toFixed(3)} kg` : ''}
                  </p>
                  <ul className="mt-1">
                    {p.lines.map((ln, i) => (
                      <li key={i} className="text-[11px]">
                        <span className="font-medium">{ln.item_unique_id}</span> {ln.item_name} — {fmt(ln.quantity)} {ln.unit_of_measure}
                        {ln.from_stock > 0 && <span className="text-emerald-600"> ({fmt(ln.from_stock)} from stock)</span>}
                        {ln.to_produce > 0 && <span className="text-amber-700"> ({fmt(ln.to_produce)} produced)</span>}
                      </li>
                    ))}
                  </ul>
                  {p.work_orders.length > 0 && (
                    <ul className="mt-1">
                      {p.work_orders.map(w => (
                        <li key={w.wo_id} className="text-[11px] text-muted-foreground">
                          <Factory className="w-3 h-3 inline mr-0.5" />
                          {w.wo_number} · {w.item_name} · {fmt(w.quantity)} · {w.status}
                          {w.route_card_number ? ` · RC ${w.route_card_number}` : ''}
                          {w.parent_wo_number ? ` · under ${w.parent_wo_number}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  {p.status !== 'dispatched' && (p.total_wos === 0 || p.draft_wos === p.total_wos) && (
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => startEdit(p)} disabled={busyPlan === p.id} data-testid={`dp-edit-${p.id}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                  )}
                  {p.draft_wos > 0 && (
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => releasePlan(p)} disabled={busyPlan === p.id} data-testid={`dp-release-${p.id}`}>
                      Release {p.draft_wos} WO
                    </Button>
                  )}
                  {p.status !== 'dispatched' && (
                    <Button size="sm" className="h-7 text-xs bg-teal-600 hover:bg-teal-700"
                      onClick={() => openDispatch(p)} disabled={busyPlan === p.id} data-testid={`dp-dispatch-${p.id}`}>
                      <Truck className="w-3 h-3 mr-1" /> Produce &amp; dispatch
                    </Button>
                  )}
                  {p.status !== 'dispatched' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                      onClick={() => setDeleteTarget(p)} disabled={busyPlan === p.id} data-testid={`dp-delete-${p.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-sm">Delete {deleteTarget?.plan_number}?</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            This removes the plan and its {deleteTarget?.draft_wos || 0} draft work order(s).
            Completed or closed work orders are kept (their stock and traceability stay intact) —
            only work orders still released / in progress must be closed or cancelled first.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700"
              onClick={() => deletePlan(deleteTarget)} disabled={!!busyPlan} data-testid="dp-confirm-delete">
              {busyPlan && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Delete plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Produce & dispatch confirmation */}
      <Dialog open={!!dispatchDialog} onOpenChange={() => setDispatchDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <PackageCheck className="w-4 h-4 text-teal-600" />
              Produce &amp; dispatch {dispatchDialog?.posting?.plan_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <p className="text-muted-foreground">
              This posts the production receipts below (bottom-up, consuming BOM stock) and then creates the
              delivery challan for {dispatchDialog?.posting?.customer_name}.
            </p>
            <div>
              <p className="font-semibold mb-1">Production to post</p>
              {(dispatchDialog?.posting?.receipts || []).length === 0 ? (
                <p className="text-muted-foreground">Nothing to produce — dispatching from stock.</p>
              ) : (
                <ul className="space-y-0.5" data-testid="dp-dispatch-receipts">
                  {dispatchDialog.posting.receipts.map(r => (
                    <li key={r.wo_id}>
                      {r.wo_number} · {r.item_name} · {fmt(r.quantity)} (level {r.level}, {r.status})
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="font-semibold mb-1">Challan lines</p>
              <ul className="space-y-0.5">
                {(dispatchDialog?.posting?.dispatch_lines || []).map((l, i) => (
                  <li key={i}>
                    {l.item_unique_id} {l.item_name} — {fmt(l.quantity)} {l.unit_of_measure}
                    {l.line_weight_kg ? ` · ${Number(l.line_weight_kg).toFixed(3)} kg` : ''}
                  </li>
                ))}
              </ul>
              {dispatchDialog?.posting?.total_weight_kg > 0 && (
                <p className="mt-1 font-semibold" data-testid="dp-dialog-total-weight">
                  Total shipment weight: {Number(dispatchDialog.posting.total_weight_kg).toFixed(3)} kg
                  {dispatchDialog.posting.show_weight_on_challan ? ' (printed on the challan)' : ''}
                </p>
              )}
            </div>
          </div>
          <div className="rounded border bg-slate-50/60 p-2.5 space-y-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer font-medium">
              <input type="checkbox" checked={!!dispatchDialog?.emailChallan}
                onChange={e => setDispatchDialog(d => ({ ...d, emailChallan: e.target.checked }))}
                className="h-3.5 w-3.5 accent-teal-600" data-testid="dp-email-challan-toggle" />
              Email challan to the customer contacts
            </label>
            {dispatchDialog?.emailChallan && (
              <div>
                <Label className="text-[11px]">To (customer contacts + programme recipients)</Label>
                <Input value={dispatchDialog?.toEmail || ''}
                  onChange={e => setDispatchDialog(d => ({ ...d, toEmail: e.target.value }))}
                  placeholder="comma separated email addresses"
                  className="h-8 text-xs" data-testid="dp-email-challan-to" />
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  The challan PDF is attached automatically; your address and the standard internal CC list are added.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDispatchDialog(null)}>Cancel</Button>
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={confirmDispatch}
              disabled={!!busyPlan} data-testid="dp-confirm-dispatch">
              {busyPlan && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              <Truck className="w-3.5 h-3.5 mr-1" /> Post &amp; create challan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
